import type { PageCMSError } from '@backmarket/http-api/src/api-specs-content/models/base-page-content'
import {
  BlockApiPropsMapSchema,
  type _ApiBlock,
} from '@backmarket/http-api/src/api-specs-content/models/block'
import { get } from '@backmarket/utils/object/get'
import type { ZodError, ZodIssue } from 'zod'

function validateBlock(block: _ApiBlock) {
  return BlockApiPropsMapSchema[block.type].safeParse(block.props)
}

function hasMissingFieldError(issue: ZodIssue) {
  if (issue.code !== 'invalid_type') return false

  return issue.message.match(/required/i)
}

function createIssueMessage(issue: ZodIssue) {
  return `${issue.message}: ${issue.path[issue.path.length - 1]}`
}

function getIssueEntryId({
  block,
  issue,
}: {
  block: _ApiBlock
  issue: ZodIssue
}) {
  if (issue.path.length === 1) return null

  const pathToClosestParent = issue.path.slice(0, issue.path.length - 1)

  return get(block.props, pathToClosestParent)?.id || null
}

function formatErrors({ block, error }: { block: _ApiBlock; error: ZodError }) {
  return error.issues.reduce((acc, issue) => {
    // Early returns to only display missing field errors
    if (!hasMissingFieldError(issue)) return acc

    return [
      ...acc,
      {
        blockId: block.id,
        entryId: getIssueEntryId({ block, issue }),
        blockName: block.type,
        message: createIssueMessage(issue),
      },
    ]
  }, [] as PageCMSError[])
}

export function validateBlocks(blocks: _ApiBlock[]): PageCMSError[] {
  return blocks.reduce((acc, block) => {
    const blockErrors = validateBlock(block)

    if (blockErrors?.success) return acc

    const formattedErrors = formatErrors({
      block,
      error: blockErrors.error,
    })

    return [...acc, ...formattedErrors]
  }, [] as PageCMSError[])
}

export function isBlockValidForRendering(block: _ApiBlock) {
  const validation = validateBlock(block)

  if (validation?.success) return true

  return validation.error.issues.every((issue) => {
    return !hasMissingFieldError(issue)
  })
}
